import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { IconButton, InputAdornment } from "@mui/material";
import { ReactNode } from "react";
import InfoTooltipIcon from "./InfoTooltipIcon";
import { FlexProps } from "./Flex";
import NumberInput from "./NumberInput";
import { CHART_DIMENSIONS } from "./app/provider/patient/chart";

const DEFAULT_STEP = 1;
const BLOCKED_KEYS = ["-", "+", ".", "e", "E"];
const DEFAULT_INFO_SUBJECT = "value";

export type StepperNumberInputProps = {
  label?: string;
  /** Reveals hover-help next to the value. */
  info?: ReactNode;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  min?: number;
  max?: number;
  step?: number;
  error?: string | boolean;
  required?: boolean;
  disabled?: boolean;
  flexprops?: FlexProps;
};

/**
 * Whole-number field with decrement/increment steppers alongside direct entry.
 *
 * @author Rajkumar Rathod
 */
const StepperNumberInput = ({
  label,
  info,
  name,
  value,
  onChange,
  onBlur,
  min,
  max,
  step = DEFAULT_STEP,
  error,
  required,
  disabled,
  flexprops,
}: StepperNumberInputProps) => {
  const numericValue = value === "" ? undefined : Number(value);

  const shift = (direction: 1 | -1) => {
    const base = numericValue ?? min ?? 0;
    const next = base + direction * step;
    if (min !== undefined && next < min) return;
    if (max !== undefined && next > max) return;
    onChange(String(next));
  };

  const isDecrementDisabled =
    disabled || (min !== undefined && (numericValue ?? min) <= min);
  const isIncrementDisabled =
    disabled ||
    (max !== undefined && numericValue !== undefined && numericValue >= max);

  return (
    <NumberInput
      label={label}
      error={error}
      required={required}
      min={min}
      max={max}
      flexprops={flexprops}
      blockedKeys={BLOCKED_KEYS}
      textFieldProps={{
        name,
        value,
        disabled,
        onBlur,
        onChange: (event) => onChange(event.target.value),
        InputProps: {
          startAdornment: (
            <InputAdornment position="start">
              <IconButton
                aria-label={`Decrease ${label ?? DEFAULT_INFO_SUBJECT}`}
                size="small"
                disabled={isDecrementDisabled}
                onClick={() => shift(-1)}
              >
                <RemoveIcon sx={{ fontSize: CHART_DIMENSIONS.iconSm }} />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {!!info && (
                <InfoTooltipIcon
                  title={label}
                  content={info}
                  ariaLabel={`About ${label ?? DEFAULT_INFO_SUBJECT}`}
                />
              )}
              <IconButton
                aria-label={`Increase ${label ?? DEFAULT_INFO_SUBJECT}`}
                size="small"
                disabled={isIncrementDisabled}
                onClick={() => shift(1)}
              >
                <AddIcon sx={{ fontSize: CHART_DIMENSIONS.iconSm }} />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

export default StepperNumberInput;
